import React from 'react'
import Popover from '@mui/material/Popover'

import { AlertHoverCard } from './AlertHoverCard'
import { alertHoverStyles } from './AlertHover.styles'
import type { AlertHoverPayload, CustomAlertItemBody } from './alertHover.types'
import type { AlertSystemType, AlertSystemItem } from '../../config/alertSystem.types'

type Props = {
  anchorEl: HTMLElement | null
  alertId: string | null
  onClose: () => void
  alertType?: AlertSystemType | null
  alertData?: AlertSystemItem | null
}

const formatBirthday = (dob?: string): string => {
  if (!dob) {
    return ''
  }
  const parts = dob.split('-')
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dob
}

export const resolveHoverData = (
  _alertId: string | null,
  alertType?: AlertSystemType | null,
  alertData?: AlertSystemItem | null
): AlertHoverPayload | null => {
  if (!alertType || !alertData || alertData.requiresFetch || !alertData.data) {
    return null
  }

  const { clientId, data, colorsOverride } = alertData

  switch (alertType) {
    case 'payment':
      return {
        id: clientId,
        type: alertType,
        body: { failed: data.failed || 0, outstanding: data.outstanding || 0 },
      }
    case 'forms':
      return {
        id: clientId,
        type: alertType,
        body: { pendingCount: data.count || 0 },
      }
    case 'notesProps':
      return {
        id: clientId,
        type: alertType,
        body: { notes: data.notes || 0, properties: data.properties || 0 },
      }
    case 'suspended':
      return {
        id: clientId,
        type: alertType,
        body: { reason: 'suspended.defaultReason' },
      }
    case 'daysAbsent':
      return {
        id: clientId,
        type: alertType,
        body: { daysAbsent: data.daysAbsent ?? 0 },
        colorsOverride,
      }
    case 'destinationFee':
      return {
        id: clientId,
        type: alertType,
        body: {},
      }
    case 'birthday':
      return {
        id: clientId,
        type: alertType,
        body: { date: formatBirthday(data.dateOfBirth) },
      }
    case 'location':
      return {
        id: clientId,
        type: alertType,
        body: { homeSiteName: data.homeSiteName ?? undefined },
      }
    case 'agreements':
      return {
        id: clientId,
        type: alertType,
        body: { count: data.count || 0 },
      }
    case 'introOffers':
      return {
        id: clientId,
        type: alertType,
        body: { count: data.count || 0 },
      }
    case 'customAlert':
      return {
        id: clientId,
        type: alertType,
        body: {
          items: Array.isArray(data.items) ? (data.items as CustomAlertItemBody[]) : [],
        },
      }
    case 'firstSession':
      return data.siteName
        ? { id: clientId, type: alertType, body: { siteName: data.siteName } }
        : null
    case 'packages':
      return null
    default: {
      const exhaustiveCheck: never = alertType
      return exhaustiveCheck
    }
  }
}

export const AlertHoverPopover: React.FC<Props> = ({
  anchorEl,
  alertId,
  onClose,
  alertType,
  alertData,
}) => {
  const open = Boolean(anchorEl) && Boolean(alertId)

  const data = open && alertId ? resolveHoverData(alertId, alertType, alertData) : null

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableRestoreFocus
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={alertHoverStyles.popover}
    >
      {data && <AlertHoverCard data={data} />}
    </Popover>
  )
}
